import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, Trash2, Pencil, FileSignature, Download, Car, Phone, IdCard } from "lucide-react";
import html2canvas from "html2canvas";
import { Letterhead } from "./Letterhead";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { SignaturePad } from "./SignaturePad";
import { SaudiRiyal, Money } from "./SaudiRiyal";
import { api, fileUrl } from "../lib/api";
import { isVehicleType } from "../lib/vehicles";
import { useAuth } from "../context/AuthContext";

const CYCLES = { daily: "يومي", weekly: "أسبوعي", monthly: "شهري" };
const empty = { name: "", iqama: "", phone: "", nationality: "", vehicle_plate: "", vehicle_type: "", rent_amount: "", pay_cycle: "monthly", start_date: "", deposit: "", contract_number: "", notes: "" };

const RenterDialog = ({ open, onOpenChange, initial, vehicles, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [mode, setMode] = useState("select"); // select | manual
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);
  useEffect(() => { if (open) { setForm(initial ? { ...empty, ...initial, rent_amount: initial.rent_amount ?? "", deposit: initial.deposit ?? "" } : empty); setMode("select"); } }, [open, initial]);

  const save = async () => {
    if (!form.name.trim()) { toast.warning("أدخل اسم المستأجر"); return; }
    setBusy(true);
    try {
      const sig = sigRef.current?.getSignature?.();
      const payload = { ...form, rent_amount: Number(form.rent_amount || 0), deposit: Number(form.deposit || 0), signature_data: sig || null };
      if (initial?.id) await api.updateRenter(initial.id, payload);
      else await api.createRenter(payload);
      toast.success("تم الحفظ"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="renter-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{initial?.id ? "تعديل مستأجر" : "إضافة حساب مستأجر"}</DialogTitle>
          <DialogDescription className="text-right">أدخل المعلومات الشخصية وبيانات الإيجار والمركبة، ثم وقّع كسند استلام.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="mb-2 text-xs font-bold text-slate-500">المعلومات الشخصية</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>الاسم</Label><Input data-testid="renter-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1"><Label>رقم الهوية/الإقامة</Label><Input data-testid="renter-iqama" dir="ltr" value={form.iqama} onChange={(e) => setForm({ ...form, iqama: e.target.value })} /></div>
              <div className="space-y-1"><Label>الجوال</Label><Input data-testid="renter-phone" dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1"><Label>الجنسية</Label><Input data-testid="renter-nationality" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="mb-2 text-xs font-bold text-slate-500">المركبة</p>
            <div className="mb-2 flex gap-2 text-xs">
              <button type="button" onClick={() => setMode("select")} className={`rounded-lg px-3 py-1 font-semibold ${mode === "select" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="renter-vehicle-mode-select">اختيار من العُهد</button>
              <button type="button" onClick={() => setMode("manual")} className={`rounded-lg px-3 py-1 font-semibold ${mode === "manual" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="renter-vehicle-mode-manual">إدخال يدوي</button>
            </div>
            {mode === "select" ? (
              <select data-testid="renter-vehicle-select" value={form.vehicle_plate}
                onChange={(e) => { const v = vehicles.find((x) => x.identifier === e.target.value); setForm({ ...form, vehicle_plate: e.target.value, vehicle_type: v?.type || form.vehicle_type }); }}
                className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">اختر مركبة</option>
                {vehicles.map((v) => <option key={v.id} value={v.identifier}>{`${v.identifier} — ${v.type}`}</option>)}
              </select>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>رقم اللوحة</Label><Input data-testid="renter-plate" dir="ltr" value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })} /></div>
                <div className="space-y-1"><Label>نوع المركبة</Label><Input data-testid="renter-vtype" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} /></div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="mb-2 text-xs font-bold text-slate-500">بيانات حساب المستأجر</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="flex items-center gap-1">قيمة الإيجار <SaudiRiyal className="text-slate-400" /></Label><Input data-testid="renter-rent" type="number" dir="ltr" value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} /></div>
              <div className="space-y-1"><Label>دورة السداد</Label>
                <select data-testid="renter-cycle" value={form.pay_cycle} onChange={(e) => setForm({ ...form, pay_cycle: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  {Object.entries(CYCLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label>تاريخ البداية</Label><Input data-testid="renter-start" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1"><Label className="flex items-center gap-1">التأمين/الوديعة <SaudiRiyal className="text-slate-400" /></Label><Input data-testid="renter-deposit" type="number" dir="ltr" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} /></div>
              <div className="space-y-1"><Label>رقم العقد</Label><Input data-testid="renter-contract" dir="ltr" value={form.contract_number} onChange={(e) => setForm({ ...form, contract_number: e.target.value })} /></div>
              <div className="space-y-1"><Label>ملاحظات</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3">
            <p className="mb-2 text-xs font-bold text-slate-500">التوقيع (سند استلام)</p>
            <SignaturePad ref={sigRef} />
            {initial?.signature_id && <p className="mt-1 text-[11px] text-emerald-600">يوجد توقيع محفوظ — وقّع مجددًا فقط إذا رغبت باستبداله.</p>}
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="renter-save" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} حفظ
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReceiptDialog = ({ renter, onClose }) => {
  const ref = useRef(null);
  const { company } = useAuth();
  const [busy, setBusy] = useState(false);
  const row = (l, v) => v ? <div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">{l}</span><span className="font-semibold text-slate-800">{v}</span></div> : null;
  const download = async () => {
    if (!ref.current) return; setBusy(true);
    try { const c = await html2canvas(ref.current, { scale: 2, backgroundColor: "#fff", useCORS: true }); const a = document.createElement("a"); a.href = c.toDataURL("image/png"); a.download = `سند-استلام-${renter.name}.png`; a.click(); toast.success("تم تصدير سند الاستلام"); }
    catch { toast.error("تعذّر إنشاء الصورة"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!renter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="renter-receipt-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading text-lg">سند استلام مركبة</DialogTitle></DialogHeader>
        <div ref={ref} className="rounded-2xl border border-slate-200 bg-white p-5" dir="rtl">
          <Letterhead />
          <div className="mb-3 border-b-2 border-blue-600 pb-2 text-center">
            <p className="font-heading text-lg font-extrabold text-blue-700">سند استلام مركبة مؤجّرة</p>
            <p className="text-xs text-slate-400">{new Date().toLocaleDateString("ar-SA")}</p>
          </div>
          <div className="text-sm">
            {row("الاسم", renter.name)}
            {row("رقم الهوية/الإقامة", renter.iqama)}
            {row("الجوال", renter.phone)}
            {row("الجنسية", renter.nationality)}
            {row("المركبة", [renter.vehicle_plate, renter.vehicle_type].filter(Boolean).join(" — "))}
            {row("قيمة الإيجار", renter.rent_amount ? <span className="inline-flex items-center gap-1">{Number(renter.rent_amount).toLocaleString("en-US")} <SaudiRiyal /> / {CYCLES[renter.pay_cycle] || ""}</span> : null)}
            {row("التأمين/الوديعة", renter.deposit ? <span className="inline-flex items-center gap-1">{Number(renter.deposit).toLocaleString("en-US")} <SaudiRiyal /></span> : null)}
            {row("تاريخ البداية", renter.start_date)}
            {row("رقم العقد", renter.contract_number)}
            {row("ملاحظات", renter.notes)}
          </div>
          <div className="mt-4 flex items-end justify-between gap-4">
            <div>
              <p className="mb-1 text-xs text-slate-500">توقيع المستلم (المستأجر)</p>
              {renter.signature_id ? <img src={fileUrl(renter.signature_id)} alt="توقيع" crossOrigin="anonymous" className="h-20 rounded border border-slate-200 bg-white object-contain" /> : <div className="h-20 w-40 rounded border border-dashed border-slate-300" />}
            </div>
            <div className="text-center">
              <div className="min-h-[80px] flex items-end justify-center">
                {company?.signature_id
                  ? <img src={fileUrl(company.signature_id)} alt="توقيع المسؤول" crossOrigin="anonymous" className="h-24 object-contain mx-auto" />
                  : <div className="h-16 w-48 border-b border-slate-400" />}
              </div>
              <div className="border-t border-slate-400 mt-0.5 pt-1">
                <p className="text-sm font-bold text-slate-700">{company?.official_title || "المسؤول"}{company?.official_name ? `: ${company.official_name}` : ""}</p>
              </div>
              {company?.stamp_id && <img src={fileUrl(company.stamp_id)} alt="ختم" crossOrigin="anonymous" className="h-44 object-contain mx-auto mt-2" />}
              <p className="text-[11px] text-slate-400 mt-1">توقيع المسؤول والختم</p>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="renter-receipt-download" disabled={busy} onClick={download} className="bg-blue-600 hover:bg-blue-700 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تحميل كصورة</Button>
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RentersPanel = ({ branchOptions = [], canEdit }) => {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [edit, setEdit] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([api.listRenters(), api.custodyAssets("all")]);
      setItems(r.items || []);
      setVehicles((a || []).filter((x) => isVehicleType(x.type)));
    } catch { toast.error("تعذّر التحميل"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { try { await api.deleteRenter(id); toast.success("تم الحذف"); load(); } catch { toast.error("تعذّر الحذف"); } };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="renters-panel" dir="rtl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">حسابات مستأجري المركبات مع سند استلام موقّع</p>
        {canEdit && <Button data-testid="add-renter-button" onClick={() => { setEdit(null); setDialog(true); }} className="bg-blue-600 hover:bg-blue-700 gap-1.5"><UserPlus className="h-4 w-4" /> إضافة مستأجر</Button>}
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400" data-testid="renters-empty">لا توجد حسابات مستأجرين — أضف أول مستأجر</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {items.map((r) => (
            <div key={r.id} data-testid={`renter-card-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-700">{(r.name || "؟").charAt(0)}</div>
                  <div>
                    <p className="font-heading font-bold text-slate-800">{r.name}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-2">{r.iqama && <span className="inline-flex items-center gap-1"><IdCard className="h-3 w-3" />{r.iqama}</span>}{r.phone && <span className="inline-flex items-center gap-1" dir="ltr"><Phone className="h-3 w-3" />{r.phone}</span>}</p>
                  </div>
                </div>
                {r.signature_id && <FileSignature className="h-4 w-4 text-emerald-500" title="موقّع" />}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-slate-50 p-2"><span className="text-slate-400">المركبة</span><div className="font-semibold text-slate-700 flex items-center gap-1" dir="ltr"><Car className="h-3 w-3" />{[r.vehicle_plate, r.vehicle_type].filter(Boolean).join(" · ") || "—"}</div></div>
                <div className="rounded-lg bg-slate-50 p-2"><span className="text-slate-400">الإيجار</span><div className="font-semibold text-slate-700"><Money value={r.rent_amount} /> / {CYCLES[r.pay_cycle] || ""}</div></div>
                {r.nationality && <div className="rounded-lg bg-slate-50 p-2"><span className="text-slate-400">الجنسية</span><div className="font-semibold text-slate-700">{r.nationality}</div></div>}
                {r.contract_number && <div className="rounded-lg bg-slate-50 p-2"><span className="text-slate-400">رقم العقد</span><div className="font-semibold text-slate-700" dir="ltr">{r.contract_number}</div></div>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" variant="outline" data-testid={`renter-receipt-${r.id}`} onClick={() => setReceipt(r)} className="gap-1.5 border-blue-200 text-blue-700"><FileSignature className="h-3.5 w-3.5" /> سند استلام</Button>
                {canEdit && <button data-testid={`renter-edit-${r.id}`} onClick={() => { setEdit(r); setDialog(true); }} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>}
                {canEdit && <button data-testid={`renter-delete-${r.id}`} onClick={() => del(r.id)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <RenterDialog open={dialog} onOpenChange={setDialog} initial={edit} vehicles={vehicles} onSaved={load} />
      {receipt && <ReceiptDialog renter={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
};
